import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Button, Input, Space, theme } from 'antd';
import type { EventBus, PDFFindController } from 'pdfjs-dist/web/pdf_viewer.mjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { getLoadedPdfjsViewer } from './pdfjsViewerModule';

interface Props {
  eventBus: EventBus | null;
  findController: PDFFindController | null;
}

interface MatchesCount {
  current: number;
  total: number;
}

const inputStyle = css`
  width: 220px;
`;

export const FindControls: React.FC<Props> = ({ eventBus, findController }) => {
  const { token } = theme.useToken();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 250);
  const [matches, setMatches] = useState<MatchesCount>({ current: 0, total: 0 });
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!eventBus) {
      return;
    }
    const onMatchesCount = ({ matchesCount }: { matchesCount: MatchesCount }) => setMatches(matchesCount);
    const onControlState = ({ state, matchesCount }: { state: number; matchesCount: MatchesCount }) => {
      setMatches(matchesCount);
      // The module is loaded by the time any find event can fire.
      setNotFound(state === getLoadedPdfjsViewer()?.FindState.NOT_FOUND);
    };

    eventBus.on('updatefindmatchescount', onMatchesCount);
    eventBus.on('updatefindcontrolstate', onControlState);
    return () => {
      eventBus.off('updatefindmatchescount', onMatchesCount);
      eventBus.off('updatefindcontrolstate', onControlState);
    };
  }, [eventBus]);

  // The controller listens for a single "find" event; type '' starts a fresh
  // search, type 'again' steps through the existing matches.
  const dispatchFind = (type: '' | 'again', findPrevious: boolean, nextQuery: string) => {
    if (!eventBus || !findController) {
      return;
    }
    eventBus.dispatch('find', {
      source: null,
      type,
      query: nextQuery,
      caseSensitive: false,
      entireWord: false,
      highlightAll: true,
      findPrevious,
      matchDiacritics: false,
    });
  };

  // Searching runs off the debounced query, so no Enter is needed. 250 ms matches
  // the other search fields in labrador-ui.
  useEffect(() => {
    if (!eventBus || !findController) {
      return;
    }
    if (!debouncedQuery) {
      setMatches({ current: 0, total: 0 });
      setNotFound(false);
    }
    eventBus.dispatch('find', {
      source: null,
      type: '',
      query: debouncedQuery,
      caseSensitive: false,
      entireWord: false,
      highlightAll: true,
      findPrevious: false,
      matchDiacritics: false,
    });
  }, [debouncedQuery, eventBus, findController]);

  // Inside the field, as browser find bars do: it costs no toolbar width and cannot
  // shift the group, since the input itself is fixed.
  const counterStyle = css`
    color: ${token.colorTextSecondary};
    white-space: nowrap;
    font-size: ${token.fontSizeSM}px;
  `;

  const hasMatches = matches.total > 0;

  return (
    <Space size={token.paddingXS}>
      <Input
        className={inputStyle}
        placeholder="Im Dokument suchen"
        value={query}
        disabled={!findController}
        allowClear
        suffix={
          query && (hasMatches || notFound) ? (
            <span className={counterStyle}>{hasMatches ? `${matches.current}/${matches.total}` : '0/0'}</span>
          ) : (
            // A zero-width space keeps the suffix slot occupied, so the text does not
            // jump sideways when the counter appears.
            <span className={counterStyle}>&#8203;</span>
          )
        }
        onChange={event => setQuery(event.target.value)}
        onPressEnter={() => dispatchFind('again', false, query)}
      />
      <Button
        icon={<UpOutlined />}
        aria-label="Vorheriger Treffer"
        disabled={!findController || !hasMatches}
        onClick={() => dispatchFind('again', true, query)}
      />
      <Button
        icon={<DownOutlined />}
        aria-label="Nächster Treffer"
        disabled={!findController || !hasMatches}
        onClick={() => dispatchFind('again', false, query)}
      />
    </Space>
  );
};
